import { Link as RouterLink } from 'react-router-dom';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { toPipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { formatEntityTagsLabel } from '@/features/shared/tags';
import { s } from '@/theme/tokens';
import { InfoField } from '@/ui/composites/InfoField';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { LayoutGrid, LayoutGridItem } from '@/ui/layout/LayoutGrid';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import {
  Chip,
  formatStatusLabel,
  Link,
  resolveStatusChipTone,
  Surface,
  Text,
} from '@/ui/primitives';
import type { BuildDto, ExecutionRunDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

export type ExecutionRunDetailsPanelsProps = {
  executionRun: ExecutionRunDto;
  build: BuildDto | null;
};

export const ExecutionRunDetailsPanels = ({
  executionRun,
  build,
}: ExecutionRunDetailsPanelsProps): ReactElement => {
  const noDataLabel = 'No data';
  const noDataText = (
    <Text role="body" component="span" color="textSecondary">
      {noDataLabel}
    </Text>
  );
  const hasBuild = Boolean(build?.id);
  const startedLabel = formatDate(executionRun.startedAt, { includeTime: true }) || noDataText;
  const finishedLabel = formatDate(executionRun.finishedAt, { includeTime: true }) || noDataText;
  const durationLabel =
    executionRun.durationMs !== null && executionRun.durationMs !== undefined
      ? formatDuration(executionRun.durationMs)
      : noDataText;
  const projectLabel =
    executionRun.projectName ?? executionRun.projectSlug ?? executionRun.projectId;
  const tagsLabel = formatEntityTagsLabel(executionRun.tags);
  const resultsFileLabel =
    executionRun.resultsFileName ??
    executionRun.resultsFileSlug ??
    executionRun.resultsFileId ??
    noDataText;
  const resultsFileHref = executionRun.resultsFileSlug
    ? `/results-files/${executionRun.resultsFileSlug}`
    : executionRun.resultsFileId
      ? `/results-files/${executionRun.resultsFileId}`
      : null;
  const buildStatusLabel = build?.status ? formatStatusLabel(build.status) : noDataText;
  const pipelineLabel = build?.pipelineName ?? build?.pipelineId ?? noDataText;
  const pipelineSlug = build?.pipelineName ? toPipelineSlug(build.pipelineName) : null;
  const pipelineSegment =
    pipelineSlug && pipelineSlug.length > 0 ? pipelineSlug : (build?.pipelineId ?? null);
  const buildRunNumber = resolveBuildRunNumber(build?.buildKey);
  const pipelineHref = pipelineSegment ? `/pipelines/${pipelineSegment}` : '/pipelines';
  const buildHref =
    pipelineSegment && buildRunNumber !== null
      ? `/pipelines/${pipelineSegment}/runs/${buildRunNumber}`
      : pipelineHref;
  const buildLabel = build?.buildKey ?? build?.id ?? noDataText;

  return (
    <Section paddingY="md">
      <Surface padding="lg" border>
        <LayoutGrid gap="md">
          <LayoutGridItem xs={12} sm={hasBuild ? 4 : 6}>
            <Stack
              gap="sm"
              sx={{ paddingRight: s('md'), borderRight: 0.5, borderColor: 'divider' }}
            >
              <InfoField label="Execution Run ID" value={executionRun.id} gap="none" />
              <InfoField
                label="Results File"
                value={
                  resultsFileHref ? (
                    <Link
                      component={RouterLink}
                      to={resultsFileHref}
                      underline="hover"
                      color="inherit"
                    >
                      {resultsFileLabel}
                    </Link>
                  ) : (
                    resultsFileLabel
                  )
                }
                gap="none"
              />
              <InfoField label="Project" value={projectLabel} gap="none" />
              <InfoField label="Tags" value={tagsLabel} gap="none" />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem xs={12} sm={hasBuild ? 4 : 6}>
            <Stack
              gap="sm"
              sx={{
                paddingX: s('md'),
                borderRight: hasBuild ? 0.5 : 0,
                borderColor: 'divider',
              }}
            >
              <InfoField label="Started" value={startedLabel} gap="none" />
              <InfoField label="Finished" value={finishedLabel} gap="none" />
              <InfoField label="Duration" value={durationLabel} gap="none" />
            </Stack>
          </LayoutGridItem>
          {hasBuild ? (
            <LayoutGridItem xs={12} sm={4}>
              <Stack gap="sm" sx={{ paddingLeft: s('md') }}>
                <InfoField
                  label="Build"
                  value={
                    build?.id ? (
                      <Link component={RouterLink} to={buildHref} underline="hover" color="inherit">
                        {buildLabel}
                      </Link>
                    ) : (
                      buildLabel
                    )
                  }
                  gap="none"
                />
                <InfoField
                  label="Pipeline"
                  value={
                    pipelineSegment ? (
                      <Link
                        component={RouterLink}
                        to={pipelineHref}
                        underline="hover"
                        color="inherit"
                      >
                        {pipelineLabel}
                      </Link>
                    ) : (
                      pipelineLabel
                    )
                  }
                  gap="none"
                />
                <InfoField
                  label="Build Status"
                  value={
                    build?.status ? (
                      <Chip
                        label={buildStatusLabel}
                        tone={resolveStatusChipTone(build.status)}
                        size="xs"
                        transform="uppercase"
                      />
                    ) : (
                      buildStatusLabel
                    )
                  }
                  gap="none"
                />
              </Stack>
            </LayoutGridItem>
          ) : null}
        </LayoutGrid>
      </Surface>
    </Section>
  );
};
